import React from "react";
import {
  ArrowSquareOutIcon,
  DownloadSimpleIcon,
  GearIcon,
} from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { ProjectActionCard } from "./ProjectActionCard";
import { ProjectSectionCard } from "./ProjectSectionCard";
import { useOpenPublication } from "../hooks/useOpenPublication";
import { isProjectHeyGenius, hasHeyGeniusDocTag } from "../helpers/helper";
import { ProjectTag } from "pages/media/types";

interface ProjectShareExportDetails {
  projectId: string;
  projectType?: string;
  mediaId?: string;
  mediaUrl?: string | null;
  instanceId?: string;
  instanceSlug?: string;
  organizationSlug?: string;
  mediaStatus?: string;
  isLive?: boolean;
  tags?: ProjectTag[];
  isAudioOnly?: boolean;
}

interface ProjectShareExportProps {
  projectDetails: ProjectShareExportDetails;
  disableOpenExperience?: boolean;
}

export const ProjectShareExport: React.FC<ProjectShareExportProps> = ({
  projectDetails,
  disableOpenExperience,
}) => {
  const t = useTranslate();

  const {
    projectId,
    projectType,
    mediaId,
    mediaUrl,
    instanceId,
    instanceSlug,
    organizationSlug,
    mediaStatus,
    isLive = false,
    tags,
    isAudioOnly,
  } = projectDetails;

  const isDocHeyGenius = hasHeyGeniusDocTag(tags);

  const { openPublication } = useOpenPublication({
    projectId,
    publicationId: instanceId,
    requiresAccessToken: !isLive,
    instanceSlug,
    organizationSlug,
    isDocHeyGenius,
    isAudioOnly,
    mediaStatus,
    isLive,
  });

  const handleOpenExperience = () => {
    if (isProjectHeyGenius(projectType)) {
      window.open(`/media/projects/${projectId}/heygenius/preview`);
    } else {
      openPublication().catch((error) => {
        console.error("Error opening docustream:", error);
      });
    }
  };

  const quickEditItems = [
    {
      key: "openExperience",
      icon: <ArrowSquareOutIcon size={20} />,
      label: t("media.projects.share.audience"),
      onClick: handleOpenExperience,
      disabled: disableOpenExperience,
    },
    {
      key: "download",
      icon: <DownloadSimpleIcon size={20} />,
      label: t("media.components.ProjectShareExport.download"),
      onClick: mediaUrl
        ? () => {
            globalThis.open(mediaUrl, "_blank");
          }
        : undefined,
      route: mediaUrl
        ? undefined
        : `/media/projects/${projectId}/media/${mediaId}/download`,
    },
    ...(projectType === "hey_genius"
      ? [
          {
            key: "install",
            icon: <GearIcon size={20} />,
            label: t("media.components.ProjectShareExport.install"),
            route: `/media/projects/${projectId}/heygenius/installation`,
          },
        ]
      : []),
  ];

  return (
    <ProjectSectionCard title={t("media.components.ProjectShareExport.title")}>
      {quickEditItems.map((item) => (
        <ProjectActionCard
          key={item.key}
          route={item.route}
          onClick={item.onClick}
          icon={item.icon}
          label={item.label}
          disabled={item.disabled}
        />
      ))}
    </ProjectSectionCard>
  );
};
